import { useState } from 'react'
import { useVelociraptorSettings } from '../../hooks'
import type { InstallInput } from '../../types/velociraptor-settings'

export interface VelociraptorPageState {
  settings: ReturnType<typeof useVelociraptorSettings>
  testCollectorOs: string
  setTestCollectorOs: (value: string) => void
  testCollectorQuickTriage: boolean
  setTestCollectorQuickTriage: (value: boolean) => void
  isLoading: boolean
  combinedError: string | null
  handleInstall: (input: InstallInput) => Promise<void>
  handleTestConnection: () => Promise<void>
  handleGenerateTestCollector: () => Promise<void>
  handleDownloadCollector: () => Promise<void>
  handleHeaderRefresh: () => void
}

export function useVelociraptorPageState(): VelociraptorPageState {
  const settings = useVelociraptorSettings()
  const [testCollectorOs, setTestCollectorOs] = useState('Windows')
  const [testCollectorQuickTriage, setTestCollectorQuickTriage] = useState(true)

  const handleInstall = async (input: InstallInput): Promise<void> => {
    const result = await settings.serviceControl.install(input)
    if (result?.success) {
      await settings.refetchConfig()
      await settings.refetchStatus()
    }
  }

  const handleTestConnection = async (): Promise<void> => {
    await settings.connectivity.test()
  }

  const handleGenerateTestCollector = async (): Promise<void> => {
    await settings.testCollector.generate({
      targetOs: testCollectorOs,
      useQuickTriage: testCollectorQuickTriage,
    })
  }

  const handleDownloadCollector = async (): Promise<void> => {
    const flowId = settings.testCollector.result?.flowId
    if (flowId) {
      await settings.testCollector.download(flowId)
    }
  }

  const handleHeaderRefresh = (): void => {
    void settings.refetchConfig()
    void settings.refetchStatus()
  }

  const isLoading = settings.configLoading || settings.statusLoading
  const combinedError = settings.configError ?? settings.statusError

  return {
    settings,
    testCollectorOs,
    setTestCollectorOs,
    testCollectorQuickTriage,
    setTestCollectorQuickTriage,
    isLoading,
    combinedError,
    handleInstall,
    handleTestConnection,
    handleGenerateTestCollector,
    handleDownloadCollector,
    handleHeaderRefresh,
  }
}
